/**
 * Which preset tiles this person has taken off their home screen.
 *
 * ## Why removing a preset is not removing an App
 *
 * Removing a real App calls `computeClient.removeApp(compute_id)`: the
 * ComputeInstance and every set of data inside it are deleted on the server,
 * permanently, and no amount of clicking in Discover brings them back.
 *
 * A preset has no ComputeInstance, no compute_id and no data. Plaza is ONE
 * global Lobby that every account opens; there is nothing per-user to delete.
 * So "remove" here can only mean "stop showing me this tile", and the tile
 * comes back from Discover whenever they want it. That is a launcher
 * preference, in the same family as the arrangement in `homeLayout.ts`, not a
 * destructive action wearing a lighter coat.
 *
 * This distinction is why the two paths must not share a confirmation dialog:
 * `RemoveAppConfirm` promises permanent deletion, and for a preset every word
 * of that promise would be false.
 *
 * ## Why a failure means "show it"
 *
 * `readLayout` returns `null` on failure so the launcher falls back to the
 * default order. Here the equivalent safe direction is the empty list —
 * nothing dismissed, everything shown. The two errors are not symmetrical: a
 * preset reappearing after a storage glitch is a tile somebody dismisses
 * again in one tap, while a preset silently vanishing because a read threw is
 * a feature the person can no longer find.
 *
 * ## Deliberately not talking to the API
 *
 * Same v1 boundary as the arrangement: a per-device preference that never
 * touches ato-api's schema, with every read and write confined to this file
 * so moving it to an account-level preference later changes these functions
 * and nothing in the components.
 */

/** Bumped only when the stored SHAPE changes; an old key is ignored, not migrated. */
export const DISMISSED_PRESETS_STORAGE_KEY = "ato.my-apps.dismissed-presets.v1";

/**
 * Read the dismissed preset ids.
 *
 * Always an array. Unlike the layout there is no "never touched this" state
 * worth distinguishing: having dismissed nothing and having failed to find
 * out both mean the same thing on screen, and that thing is "show the tile".
 */
export function readDismissedPresets(
  storage: Storage | null = safeStorage(),
): string[] {
  if (!storage) return [];
  let raw: string | null = null;
  try {
    raw = storage.getItem(DISMISSED_PRESETS_STORAGE_KEY);
  } catch {
    // Reading can throw, not just writing.
    return [];
  }
  if (!raw) return [];
  try {
    const parsed: unknown = JSON.parse(raw);
    // A bare array is the v1 shape. Anything else is a shape this version
    // does not know — an older schema, or something that is not ours.
    if (!Array.isArray(parsed)) return [];
    return dedupe(
      parsed.filter(
        (value): value is string =>
          typeof value === "string" && value.length > 0,
      ),
    );
  } catch {
    return [];
  }
}

/** Save the dismissed ids. A failure is silent: the tile is gone for now. */
export function writeDismissedPresets(
  ids: readonly string[],
  storage: Storage | null = safeStorage(),
): void {
  if (!storage) return;
  try {
    storage.setItem(
      DISMISSED_PRESETS_STORAGE_KEY,
      JSON.stringify(dedupe(ids)),
    );
  } catch {
    // Quota, private mode, or a disabled store. The tile is still gone for
    // this session; it just comes back on reload.
  }
}

/** Take a preset off the home screen. Returns the new dismissed list. */
export function dismissPreset(
  id: string,
  storage: Storage | null = safeStorage(),
): string[] {
  const next = dedupe([...readDismissedPresets(storage), id]);
  writeDismissedPresets(next, storage);
  return next;
}

/** Put a preset back — what Discover's Add does. Returns the new list. */
export function restorePreset(
  id: string,
  storage: Storage | null = safeStorage(),
): string[] {
  const next = readDismissedPresets(storage).filter((value) => value !== id);
  writeDismissedPresets(next, storage);
  return next;
}

function dedupe(ids: readonly string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const id of ids) {
    if (seen.has(id)) continue;
    seen.add(id);
    out.push(id);
  }
  return out;
}

/**
 * `localStorage` when it is genuinely usable.
 *
 * Merely existing is not enough: Safari in private mode has thrown on ACCESS
 * in some versions, and an embedded context can have storage blocked by
 * policy. Touching it inside try/catch here is what keeps every caller from
 * needing its own guard.
 */
function safeStorage(): Storage | null {
  try {
    if (typeof window === "undefined" || !window.localStorage) return null;
    return window.localStorage;
  } catch {
    return null;
  }
}
